import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PHARMACY_COPY, statusLabel } from '../pharmacyCopy';
import { CENTRAL_PHARMACY_PATHS, PHARMACIST_PATHS } from '../pharmacyPaths';

const copy = PHARMACY_COPY.INVENTORY;

export const INVENTORY_STATUSES = ['', 'COUNTING', 'CLOSED', 'CANCELLED'];

export function useInventoryRoutes() {
  const { pathname } = useLocation();
  const isPharmacist = pathname.startsWith('/pharmacist');
  return useMemo(() => {
    const base = isPharmacist ? PHARMACIST_PATHS : CENTRAL_PHARMACY_PATHS;
    return {
      LIST: base.INVENTORIES,
      DETAIL: base.INVENTORY_DETAIL
    };
  }, [isPharmacist]);
}

export function inventoryStatusClass(status) {
  if (status === 'COUNTING') return 'cp-status-pill--warn';
  if (status === 'CLOSED') return 'cp-status-pill--ok';
  return 'cp-status-pill--muted';
}

export function parseCountedDraft(value) {
  const t = String(value ?? '').trim();
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return NaN;
  return parseInt(t, 10);
}

export function lineVariance(theoretical, counted) {
  if (counted === null || counted === undefined || Number.isNaN(counted)) return null;
  return Number(counted) - Number(theoretical || 0);
}

export function seedCountDrafts(lines = []) {
  const next = {};
  lines.forEach((line) => {
    next[line.id] =
      line.counted_qty === null || line.counted_qty === undefined ? '' : String(line.counted_qty);
  });
  return next;
}

export function isLineDirty(line, draft) {
  const parsed = parseCountedDraft(draft);
  if (Number.isNaN(parsed)) return true;
  const saved =
    line.counted_qty === null || line.counted_qty === undefined ? null : Number(line.counted_qty);
  return parsed !== saved;
}

export function progressPct(counted, total) {
  if (!total) return 0;
  return Math.round((Number(counted) / Number(total)) * 100);
}

export function mergeKeptDrafts(lines, seeded, prev) {
  const next = { ...seeded };
  (lines || []).forEach((line) => {
    const draft = prev[line.id];
    if (draft === undefined) return;
    if (isLineDirty(line, draft)) next[line.id] = draft;
  });
  return next;
}

export function focusAdjacentCount(lineId, direction) {
  const inputs = Array.from(document.querySelectorAll('[data-inv-count]'));
  const i = inputs.findIndex((el) => el.getAttribute('data-inv-count') === String(lineId));
  if (i < 0) return;
  const next = inputs[i + direction];
  if (!next) return;
  next.focus();
  if (typeof next.select === 'function') next.select();
}

const FLOW_STEPS = [
  { id: 'OPEN', label: copy.FLOW_OPEN },
  { id: 'COUNTING', label: copy.FLOW_COUNT },
  { id: 'CLOSED', label: copy.FLOW_CLOSE }
];

export function InventoryFlow({ status }) {
  if (status === 'CANCELLED') {
    return (
      <div className="cp-order-flow cp-order-flow--cancelled" aria-label={copy.FLOW_TITLE}>
        <span className="cp-status-pill cp-status-pill--muted">{statusLabel('CANCELLED')}</span>
      </div>
    );
  }
  const active = status === 'CLOSED' ? 2 : 1;
  return (
    <ol className="cp-order-flow" aria-label={copy.FLOW_TITLE}>
      {FLOW_STEPS.map((step, i) => {
        let state = 'upcoming';
        if (i < active) state = 'done';
        else if (i === active) state = 'current';
        if (status === 'CLOSED' && i === 2) state = 'done';
        return (
          <li key={step.id} className={`cp-order-flow__step is-${state}`}>
            <span className="cp-order-flow__dot" aria-hidden>
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span className="cp-order-flow__label">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function InventoryProgress({ counted = 0, total = 0, compact = false }) {
  const pct = progressPct(counted, total);
  const label = copy.PROGRESS_LABEL.replace('{counted}', String(counted)).replace(
    '{total}',
    String(total)
  );
  return (
    <div className={`cp-inv-progress${compact ? ' cp-inv-progress--compact' : ''}`}>
      <div
        className="cp-inv-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={label}
      >
        <span className="cp-inv-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="cp-inv-progress__meta">
        <strong>{label}</strong>
        <span>{copy.PROGRESS_PCT.replace('{n}', String(pct))}</span>
      </span>
    </div>
  );
}
